import { Command } from 'cmdk';
import { Cancel01Icon, Tick02Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { controlBase } from './input.js';
import type { SelectOption } from './select.js';

interface ComboboxBaseProps {
  options: readonly SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

export interface ComboboxSingleProps extends ComboboxBaseProps {
  multiple?: false;
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** When set, shows an option that clears the selection (e.g. "Todos los proyectos"). */
  clearLabel?: string;
}

export interface ComboboxMultiProps extends ComboboxBaseProps {
  multiple: true;
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  clearLabel?: string;
}

export type ComboboxProps = ComboboxSingleProps | ComboboxMultiProps;

const CLEAR = '__ef_clear__';

/** Searchable select (cmdk list in a Radix popover). Supports single and multiple selection. */
export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(props, ref) {
  const {
    options,
    placeholder = 'Seleccionar…',
    searchPlaceholder = 'Buscar…',
    emptyText = 'Sin resultados',
    disabled,
    id,
    className,
    clearLabel,
    ...rest
  } = props;
  const aria = {
    'aria-label': rest['aria-label'],
    'aria-invalid': rest['aria-invalid'],
    'aria-describedby': rest['aria-describedby'],
    'aria-required': rest['aria-required'],
  };
  const [open, setOpen] = React.useState(false);
  const selectedValues: readonly string[] = props.multiple ? props.value : props.value ? [props.value] : [];
  const selected = options.filter((o) => selectedValues.includes(o.value));

  const summary = props.multiple
    ? selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.map((o) => o.label).join(', ')
        : `${selected.length} seleccionados`
    : (selected[0]?.label ?? placeholder);

  const choose = (value: string) => {
    if (props.multiple) {
      if (value === CLEAR) props.onValueChange([]);
      else props.onValueChange(props.value.includes(value) ? props.value.filter((v) => v !== value) : [...props.value, value]);
      return;
    }
    props.onValueChange(value === CLEAR ? null : value);
    setOpen(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        ref={ref}
        id={id}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(controlBase, 'flex items-center justify-between gap-2 text-left', selected.length === 0 && 'text-text-secondary', className)}
        {...aria}
      >
        <span className="truncate">{summary}</span>
        <Icon icon={UnfoldMoreIcon} aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[max(var(--radix-popover-trigger-width),260px)] rounded-menu border border-border-subtle bg-surface-default p-1 shadow-overlay animate-fade-in"
        >
          <Command loop className="grid gap-1" label={aria['aria-label'] ?? placeholder}>
            <Command.Input
              autoFocus
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className={cn(controlBase, 'h-9')}
            />
            <Command.List className="max-h-64 overflow-auto" aria-multiselectable={props.multiple || undefined}>
              <Command.Empty className="px-3 py-2 text-body text-text-secondary">{emptyText}</Command.Empty>
              {clearLabel ? (
                <ComboItem value={CLEAR} label={clearLabel} selected={selectedValues.length === 0} onSelect={choose} muted />
              ) : null}
              {options.map((o) => (
                <ComboItem
                  key={o.value}
                  value={o.value}
                  label={o.label}
                  description={o.description}
                  disabled={o.disabled}
                  selected={selectedValues.includes(o.value)}
                  onSelect={choose}
                  checkbox={props.multiple}
                />
              ))}
            </Command.List>
          </Command>
          {props.multiple && props.value.length > 0 ? (
            <div className="mt-1 flex items-center justify-between border-t border-border-subtle px-2 pt-2 pb-1">
              <span className="text-meta text-text-secondary">{props.value.length} seleccionados</span>
              <button
                type="button"
                onClick={() => props.onValueChange([])}
                className="inline-flex items-center gap-1 rounded-control px-2 py-1 text-meta font-medium text-brand-primary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus"
              >
                <Icon icon={Cancel01Icon} aria-hidden className="size-3.5" />
                Limpiar
              </button>
            </div>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

function ComboItem({
  value,
  label,
  description,
  disabled,
  selected,
  onSelect,
  checkbox,
  muted,
}: {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  selected: boolean;
  onSelect: (value: string) => void;
  checkbox?: boolean;
  muted?: boolean;
}) {
  return (
    <Command.Item
      value={value}
      keywords={[label, description ?? '']}
      disabled={disabled}
      onSelect={() => onSelect(value)}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-control px-3 py-2 text-body text-text-primary outline-none',
        'data-[selected=true]:bg-surface-subtle data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50',
        muted && 'text-text-secondary',
      )}
    >
      {checkbox ? (
        <span
          aria-hidden
          className={cn(
            'inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-border-control',
            selected && 'border-brand-primary bg-brand-primary text-text-on-primary',
          )}
        >
          {selected ? <Icon icon={Tick02Icon} className="size-3" /> : null}
        </span>
      ) : (
        <Icon icon={Tick02Icon} aria-hidden className={cn('size-4 shrink-0 text-brand-primary', selected ? 'opacity-100' : 'opacity-0')} />
      )}
      <span className="grid min-w-0">
        <span className="truncate">{label}</span>
        {description ? <span className="truncate text-meta text-text-secondary">{description}</span> : null}
      </span>
      {selected ? <span className="sr-only">(seleccionado)</span> : null}
    </Command.Item>
  );
}
